const DEFAULT_MAX_EXTRACTED_CHARS = 12000;

export const MAX_EXTRACTED_CHARS = Number(import.meta.env.VITE_MAX_EXTRACTED_CHARS || DEFAULT_MAX_EXTRACTED_CHARS);

export function formatCharacterCount(value) {
  return new Intl.NumberFormat("en-US").format(Math.max(0, Number(value) || 0));
}

function normalizeText(value) {
  return String(value == null ? "" : value).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

export function countExtractedCharacters(items = []) {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => sum + normalizeText(item?.rawText || item?.text || "").length, 0);
}

export function getExtractedTextLimitError(actualCharCount, maxChars = MAX_EXTRACTED_CHARS) {
  const limit = Math.max(1, Number(maxChars) || MAX_EXTRACTED_CHARS);
  const actual = Math.max(0, Number(actualCharCount) || 0);
  if (actual <= limit) return null;
  return {
    code: "text_too_long",
    title: "Text too long to process",
    body:
      "This file is longer than the current test limit. Please shorten it or split it into smaller parts and try again.",
    details: [
      `Current length: ${formatCharacterCount(actual)} characters`,
      `Recommended maximum: ${formatCharacterCount(limit)} characters`,
    ],
    retryable: false,
  };
}

function isRetryableStatus(status) {
  return [429, 502, 503, 504, 529].includes(Number(status));
}

function extractProviderMessage(cause) {
  const direct = String(cause?.message || "").trim();
  if (!direct) return "";
  try {
    const parsed = JSON.parse(direct);
    const nested = String(parsed?.error?.message || "").trim();
    return nested || direct;
  } catch {
    return direct;
  }
}

function parseRateLimitTokenDetails(message) {
  const text = String(message || "");
  const limitMatch = text.match(/Limit\s+(\d+)/i);
  const requestedMatch = text.match(/Requested\s+(\d+)/i);
  return {
    limit: limitMatch ? Number(limitMatch[1]) : null,
    requested: requestedMatch ? Number(requestedMatch[1]) : null,
  };
}

function parseRetryDelayMinutes(message) {
  const text = String(message || "");
  const delayMatch = text.match(/(?:in\s+)?(\d+)\s*m(?:\s*(\d+(?:\.\d+)?)\s*s)?/i) || text.match(/(?:in\s+)?(\d+)\s*minutes?(?:\s+and\s+(\d+(?:\.\d+)?)\s*seconds?)?/i);
  if (!delayMatch) return null;
  const minutes = Number(delayMatch[1]);
  const seconds = Number(delayMatch[2] || 0);
  if (!Number.isFinite(minutes) || minutes < 0) return null;
  const rounded = seconds >= 30 ? minutes + 1 : minutes;
  return Math.max(1, rounded);
}

export function isTemporaryProviderFailure(error) {
  const status = Number(error?.status || error?.cause?.status || 0);
  if (isRetryableStatus(status)) return true;

  const code = String(error?.code || error?.cause?.code || "").toLowerCase();
  if (
    code.includes("rate") ||
    code.includes("limit") ||
    code.includes("overload") ||
    code.includes("unavailable") ||
    code.includes("timeout")
  ) {
    return true;
  }

  const text = `${String(error?.message || "")} ${String(error?.cause?.message || "")}`.toLowerCase();
  return (
    text.includes("rate limit") ||
    text.includes("too many requests") ||
    text.includes("temporarily unavailable") ||
    text.includes("overloaded") ||
    text.includes("overload") ||
    text.includes("try again later") ||
    text.includes("timeout")
  );
}

export function createTemporaryUnavailableError(cause) {
  const providerMessage = extractProviderMessage(cause);
  const retryMinutes = parseRetryDelayMinutes(providerMessage);
  const tokenLimitLike =
    /rate\s*limit|request too large|tokens per minute|token limit|tpm|tokens per day|tpd/i.test(providerMessage);
  const body = tokenLimitLike
    ? retryMinutes
      ? `You have reached a token limit. Try again in about ${retryMinutes} minute${retryMinutes === 1 ? "" : "s"}.`
      : "You have reached a token limit. Try again in a little while."
    : "The system is busy right now. Please try again in a little while.";
  return {
    code: "temporary_unavailable",
    title: tokenLimitLike ? "You have reached a token limit" : "Processing is temporarily unavailable",
    body,
    details: [],
    message: body,
    retryable: true,
    cause,
  };
}

export function createGenericProcessingError(cause) {
  const providerMessage = extractProviderMessage(cause);
  const providerCode = String(cause?.code || "").toLowerCase();
  const status = Number(cause?.status || 0);
  if (status === 413) {
    const details = [];
    if (providerMessage) details.push(providerMessage);
    return {
      code: "request_too_large",
      title: "Something went wrong",
      body: "There was a problem generating this. Please try again.",
      details,
      message: "There was a problem generating this. Please try again.",
      retryable: false,
      cause,
    };
  }
  const looksLikeRateLimit =
    status === 429 ||
    providerCode.includes("rate_limit") ||
    status === 413 ||
    /rate\s*limit|request too large|tokens per minute|token limit|tpm|tokens per day|tpd/i.test(providerMessage);
  if (looksLikeRateLimit) {
    const next = createTemporaryUnavailableError(cause);
    return { ...next, cause };
  }
  return {
    code: "processing_failed",
    title: "Could not process this file",
    body: "Something went wrong while processing this file. Please try again.",
    details: providerMessage ? [providerMessage] : [],
    message: "Something went wrong while processing this file. Please try again.",
    retryable: true,
    cause,
  };
}

export function normalizeAIProcessingError(error) {
  if (error && typeof error === "object" && typeof error.title === "string" && typeof error.body === "string") {
    return {
      ...error,
      details: Array.isArray(error.details) ? error.details : [],
      message: typeof error.message === "string" && error.message.trim() ? error.message : error.body,
      retryable: Boolean(error.retryable),
    };
  }

  if (isTemporaryProviderFailure(error)) {
    return createTemporaryUnavailableError(error);
  }

  return createGenericProcessingError(error);
}
